import React, { useEffect, useMemo, useState } from 'react';
import { ShoppingCart, ChefHat, Loader2, Utensils, Check, RefreshCw, Sparkles, Settings2, PoundSterling, Users, Clock, Copy, Download, Lock, LockOpen, History, X, AlertTriangle } from 'lucide-react';
import MealImage from './MealImage';
import { Card } from '../plan/shared';
import { api } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const SUPERMARKETS = ['Tesco', "Sainsbury's", 'Aldi', 'Lidl', 'Morrisons', 'Waitrose', 'Asda', 'Booths'];
const DIETS = [
  { id: 'balanced', label: 'Balanced' },
  { id: 'high_protein', label: 'High protein' },
  { id: 'high_fibre', label: 'High fibre' },
  { id: 'low_carb', label: 'Low carb' },
  { id: 'mediterranean', label: 'Mediterranean' },
  { id: 'vegetarian', label: 'Vegetarian' },
  { id: 'vegan', label: 'Vegan' },
  { id: 'gluten_free', label: 'Gluten free' },
];
const PREPS = [
  { id: 'quick', label: 'Quick', hint: '≤15 min' },
  { id: 'longer', label: 'Longer', hint: '20–40 min' },
  { id: 'mix', label: 'Mix', hint: 'fast wknights, richer wknds' },
];

const SetupPanel = ({ prefs, setPrefs, onSave, saving }) => (
  <div className="space-y-4" data-testid="meal-planner-setup">
    <div>
      <label className="text-[10px] uppercase tracking-wider font-black text-zinc-400 mb-2 block">Supermarket</label>
      <div className="grid grid-cols-3 gap-2">
        {SUPERMARKETS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setPrefs({ ...prefs, supermarket: s })}
            data-testid={`mp-supermarket-${s.toLowerCase().replace(/[^a-z]/g,'')}`}
            className={`h-11 px-3 rounded-xl text-xs font-black uppercase tracking-wide border transition-colors ${
              prefs.supermarket === s ? 'bg-[#BEFF38] text-black border-[#BEFF38]' : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
            }`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>

    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className="text-[10px] uppercase tracking-wider font-black text-zinc-400 mb-2 flex items-center gap-1"><PoundSterling className="w-3 h-3" /> Weekly budget</label>
        <input
          type="number"
          inputMode="decimal"
          min="10"
          max="500"
          value={prefs.weekly_budget_gbp ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            // Allow empty string while user is typing; only commit numeric.
            if (raw === '') { setPrefs({ ...prefs, weekly_budget_gbp: '' }); return; }
            const n = parseFloat(raw);
            if (!Number.isNaN(n)) setPrefs({ ...prefs, weekly_budget_gbp: n });
          }}
          onBlur={() => {
            if (prefs.weekly_budget_gbp === '' || prefs.weekly_budget_gbp == null) {
              setPrefs({ ...prefs, weekly_budget_gbp: 60 });
            }
          }}
          data-testid="mp-budget-input"
          className="w-full bg-zinc-900 border border-zinc-800 rounded-lg h-11 px-3 text-white text-sm focus:border-[#BEFF38] focus:outline-none"
        />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider font-black text-zinc-400 mb-2 flex items-center gap-1"><Users className="w-3 h-3" /> Household</label>
        <input
          type="number"
          inputMode="numeric"
          min="1"
          max="8"
          value={prefs.household_size ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') { setPrefs({ ...prefs, household_size: '' }); return; }
            const n = parseInt(raw, 10);
            if (!Number.isNaN(n)) setPrefs({ ...prefs, household_size: n });
          }}
          onBlur={() => {
            if (prefs.household_size === '' || prefs.household_size == null || prefs.household_size < 1) {
              setPrefs({ ...prefs, household_size: 1 });
            }
          }}
          data-testid="mp-household-input"
          className="w-full bg-zinc-900 border border-zinc-800 rounded-lg h-11 px-3 text-white text-sm focus:border-[#BEFF38] focus:outline-none"
        />
      </div>
    </div>

    {(() => {
      // Realistic per-person weekly minimums for macro-hitting food.
      const mins = { "Aldi": 30, "Lidl": 30, "Asda": 40, "Tesco": 42, "Morrisons": 45, "Sainsbury's": 50, "Waitrose": 60, "Booths": 60 };
      const min = mins[prefs.supermarket] || 40;
      const household = Number(prefs.household_size) || 1;
      const budget = Number(prefs.weekly_budget_gbp) || 0;
      const need = min * household;
      if (budget > 0 && budget < need) {
        return (
          <div className="rounded-lg border border-[#ff7a1a]/40 bg-[#ff7a1a]/10 px-3 py-2.5 text-[11px] leading-relaxed text-[#ffb680]" data-testid="mp-budget-warning">
            <span className="font-black uppercase tracking-wider text-[#ff7a1a]">Heads up</span> — £{budget} at {prefs.supermarket}
            {household > 1 ? ` for ${household} people` : ''} is below the realistic minimum (~£{need}). We&apos;ll build the cheapest plan we can, but expect to run over. Try Aldi/Lidl for £{Math.round(mins["Aldi"] * household)}+.
          </div>
        );
      }
      return null;
    })()}

    <div>
      <label className="text-[10px] uppercase tracking-wider font-black text-zinc-400 mb-2 block">Diet slants <span className="text-zinc-500 normal-case font-normal tracking-normal">· pick as many as apply</span></label>
      <div className="grid grid-cols-2 gap-2">
        {DIETS.map((d) => {
          const selected = (prefs.diet_slants || []).includes(d.id);
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => {
                const current = new Set(prefs.diet_slants || []);
                if (selected) {
                  current.delete(d.id);
                } else {
                  current.add(d.id);
                  // Sensible auto-clean: vegan overrides vegetarian, both override balanced
                  if (d.id === 'vegan') current.delete('vegetarian');
                  if (d.id === 'vegetarian') current.delete('vegan');
                  if (d.id !== 'balanced') current.delete('balanced');
                }
                // Empty selection reverts to balanced so the LLM always has a directive
                const next = current.size ? Array.from(current) : ['balanced'];
                setPrefs({ ...prefs, diet_slants: next });
              }}
              data-testid={`mp-diet-${d.id}`}
              aria-pressed={selected}
              className={`h-10 px-3 rounded-lg text-[11px] font-black uppercase tracking-wide border transition-colors ${
                selected ? 'bg-[#ff7a1a]/15 text-[#ff7a1a] border-[#ff7a1a]' : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
              }`}
            >
              {d.label}
            </button>
          );
        })}
      </div>
    </div>

    <div>
      <label className="text-[10px] uppercase tracking-wider font-black text-zinc-400 mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Prep style</label>
      <div className="grid grid-cols-3 gap-2">
        {PREPS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPrefs({ ...prefs, prep_style: p.id })}
            data-testid={`mp-prep-${p.id}`}
            className={`h-14 px-2 rounded-lg text-[11px] font-black uppercase tracking-wide border flex flex-col items-center justify-center gap-0.5 ${
              prefs.prep_style === p.id ? 'bg-[#BEFF38] text-black border-[#BEFF38]' : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
            }`}
          >
            <span>{p.label}</span>
            <span className={`text-[9px] font-medium normal-case tracking-normal ${prefs.prep_style === p.id ? 'text-black/60' : 'text-zinc-500'}`}>{p.hint}</span>
          </button>
        ))}
      </div>
    </div>

    <div>
      <label className="text-[10px] uppercase tracking-wider font-black text-zinc-400 mb-2 block">Snacks per day</label>
      <div className="grid grid-cols-3 gap-2">
        {[0, 1, 2].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => setPrefs({ ...prefs, snacks_per_day: n })}
            data-testid={`mp-snacks-${n}`}
            className={`h-10 px-3 rounded-lg text-[11px] font-black uppercase tracking-wide border ${
              prefs.snacks_per_day === n ? 'bg-[#BEFF38] text-black border-[#BEFF38]' : 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:border-zinc-700'
            }`}
          >
            {n === 0 ? 'No snacks' : n === 1 ? '1 snack' : '2 snacks'}
          </button>
        ))}
      </div>
    </div>

    <button
      type="button"
      onClick={onSave}
      disabled={saving}
      data-testid="mp-save-prefs"
      className="w-full h-12 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
    >
      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
      Save preferences
    </button>
  </div>
);

const DayView = ({ day, plan, dayIndex, onEaten, eatenSet, onRegenerate, regeneratingKey }) => (
  <div className="space-y-2" data-testid={`mp-day-${day.date}`}>
    <div className="flex items-baseline justify-between px-1">
      <p className="text-white font-black text-sm uppercase tracking-wide">
        {new Date(day.date).toLocaleDateString('en-GB', { weekday: 'long' })}
      </p>
      <p className="text-[10px] text-zinc-500">
        {day.calories} kcal · {day.protein_g}p / {day.carbs_g}c / {day.fats_g}f
      </p>
    </div>
    {day.recipes.map((r, i) => {
      const eatenKey = `${day.date}|${r.name}`;
      const isEaten = eatenSet.has(eatenKey);
      const regenKey = `${dayIndex}-${i}`;
      const isRegen = regeneratingKey === regenKey;
      return (
        <div
          key={`${day.date}-${i}`}
          data-testid={`mp-recipe-${r.slot}`}
          className="rounded-xl border border-zinc-800 bg-[#0b0c10] p-3"
        >
          <MealImage
            name={r.name}
            hint={Array.isArray(r.ingredients) ? r.ingredients.slice(0, 3).map((ing) => `${ing.quantity}${ing.unit} ${ing.name}`).join(', ') : ''}
            className="mb-3"
            aspect="aspect-[16/9]"
          />
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-[9px] uppercase tracking-wider text-[#ff7a1a] font-black">{r.slot} · {r.prep_minutes}min</p>
              <p className="text-white font-bold text-sm mt-0.5">{r.name}</p>
              <p className="text-[10px] text-zinc-500 mt-0.5">{r.calories} kcal · {r.protein_g}p / {r.carbs_g}c / {r.fats_g}f</p>
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <button
                type="button"
                onClick={() => onEaten(day.date, r.name)}
                disabled={isEaten}
                data-testid={`mp-eat-${r.slot}`}
                className={`h-8 px-3 rounded-full text-[10px] font-black uppercase tracking-wider whitespace-nowrap ${
                  isEaten
                    ? 'bg-[#BEFF38]/20 text-[#BEFF38] border border-[#BEFF38]/40'
                    : 'bg-[#BEFF38] text-black hover:bg-[#a6e62e]'
                }`}
              >
                {isEaten ? '✓ Eaten' : 'I ate this'}
              </button>
              <button
                type="button"
                onClick={() => onRegenerate(dayIndex, i, r.name)}
                disabled={isRegen}
                data-testid={`mp-regen-${r.slot}`}
                className="h-7 px-2.5 rounded-full text-[10px] font-black uppercase tracking-wider whitespace-nowrap bg-zinc-800 text-zinc-200 hover:bg-zinc-700 border border-zinc-700 disabled:opacity-50 inline-flex items-center gap-1 justify-center"
                title={`Swap this ${r.slot} for a different one — keeps the rest of the week intact`}
              >
                {isRegen ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Swap
              </button>
            </div>
          </div>
          {r.ingredients?.length > 0 && (
            <details className="mt-2">
              <summary className="text-[10px] uppercase tracking-wider text-zinc-500 cursor-pointer hover:text-zinc-300">
                Ingredients · Method
              </summary>
              <ul className="mt-2 text-[11px] text-zinc-300 space-y-0.5">
                {r.ingredients.map((ing, j) => (
                  <li key={j}>
                    <span className="text-zinc-500">{ing.quantity}{ing.unit} </span>
                    {ing.name}
                  </li>
                ))}
              </ul>
              {r.method && <p className="mt-2 text-[11px] text-zinc-300 leading-relaxed">{r.method}</p>}
            </details>
          )}
        </div>
      );
    })}
  </div>
);

const ShoppingListView = ({ items, budget, total, supermarket, priceStatus, liveCount, onRefresh, refreshing }) => {
  const { toast } = useToast();
  const byAisle = useMemo(() => {
    const g = {};
    items.forEach((it) => { (g[it.aisle] = g[it.aisle] || []).push(it); });
    return g;
  }, [items]);
  const overBudget = total != null && total > budget;
  const diff = total != null ? total - budget : 0;
  const pending = priceStatus === 'pending';
  const derivedCount = useMemo(() => items.filter((it) => it.price_source === 'derived').length, [items]);

  const buildText = () => {
    const lines = [];
    lines.push(`FreeFitFuel — Shopping list · ${supermarket}`);
    lines.push(`Estimated total: £${total != null ? total.toFixed(2) : '—'}  (budget £${Number(budget).toFixed(0)})`);
    lines.push(''.padEnd(48, '-'));
    for (const [aisle, list] of Object.entries(byAisle)) {
      lines.push('');
      lines.push(aisle.toUpperCase());
      for (const it of list) {
        const q = `${it.quantity}${it.unit}`;
        const price = it.est_price_gbp != null ? `£${Number(it.est_price_gbp).toFixed(2)}` : '';
        lines.push(`  [ ] ${it.name.padEnd(28)} ${q.padEnd(10)} ${price}`);
      }
    }
    lines.push('');
    lines.push('Generated by FreeFitFuel · freefitfuel.app');
    return lines.join('\n');
  };

  const handleCopy = async () => {
    const text = buildText();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      toast({ title: 'Copied', description: 'Shopping list is on your clipboard — paste into Notes or anywhere.' });
    } catch {
      toast({ title: 'Copy failed', description: 'Your browser blocked clipboard access. Try Download instead.', variant: 'destructive' });
    }
  };

  const handleDownload = () => {
    const text = buildText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const today = new Date().toISOString().slice(0, 10);
    a.href = url; a.download = `shopping-list-${supermarket.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-${today}.txt`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast({ title: 'Downloaded', description: 'Saved as a plain-text file — ready to open in Notes or share.' });
  };

  return (
    <div className="space-y-4" data-testid="mp-shopping-list">
      <div className="rounded-xl border border-zinc-800 bg-[#0b0c10] p-3">
        <div className="flex items-baseline justify-between">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">
            {pending ? 'Fetching live prices…' : (liveCount > 0 ? 'Estimated total' : 'Estimated total')} · {supermarket}
          </p>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Budget £{Number(budget).toFixed(0)}</p>
        </div>
        <div className="flex items-baseline justify-between mt-1">
          <p
            className={`font-black text-2xl ${overBudget ? 'text-[#ff7a1a]' : 'text-[#BEFF38]'}`}
            data-testid="mp-shopping-total"
          >
            £{total != null ? total.toFixed(2) : '—'}
          </p>
          {total != null && (
            <p className={`text-[11px] font-bold uppercase tracking-wider ${overBudget ? 'text-[#ff7a1a]' : 'text-zinc-500'}`}>
              {overBudget ? `£${diff.toFixed(2)} over` : `£${Math.abs(diff).toFixed(2)} under`}
            </p>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <p className="text-[10px] text-zinc-500 leading-snug flex-1 pr-2">
            {pending ? (
              <span className="inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Fetching live prices…</span>
            ) : liveCount > 0 ? (
              <>
                <span className="text-[#BEFF38] font-bold">{liveCount}</span> of {items.length} items priced live
                {derivedCount > 0 && supermarket !== "Sainsbury's" && (
                  <> · <span className="text-sky-300 font-bold">{derivedCount}</span> ≈ scaled from live Sainsbury&apos;s</>
                )}
                . Rest are baseline estimates (marked <span className="text-zinc-300">~</span>).
              </>
            ) : (
              <>Live prices unavailable — using UK own-brand estimates × {supermarket} multiplier. Items marked <span className="text-zinc-300">~</span> are best-guess.</>
            )}
          </p>
          <div className="shrink-0 flex items-center gap-1.5" data-testid="mp-shopping-actions">
            <button
              type="button"
              onClick={handleCopy}
              data-testid="mp-copy-list"
              title="Copy shopping list to clipboard"
              className="h-8 w-8 rounded-full text-[10px] font-black uppercase tracking-wider bg-zinc-900 border border-zinc-800 text-zinc-200 hover:border-zinc-700 inline-flex items-center justify-center"
            >
              <Copy className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={handleDownload}
              data-testid="mp-download-list"
              title="Download as .txt"
              className="h-8 w-8 rounded-full text-[10px] font-black uppercase tracking-wider bg-zinc-900 border border-zinc-800 text-zinc-200 hover:border-zinc-700 inline-flex items-center justify-center"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing || pending}
              data-testid="mp-refresh-prices"
              className="h-8 px-3 rounded-full text-[10px] font-black uppercase tracking-wider bg-zinc-900 border border-zinc-800 text-zinc-200 hover:border-zinc-700 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <RefreshCw className={`w-3 h-3 ${refreshing || pending ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>
        </div>
      </div>
      {Object.entries(byAisle).map(([aisle, list]) => (
        <div key={aisle} data-testid={`mp-aisle-${aisle}`}>
          <p className="text-[10px] uppercase tracking-wider font-black text-[#ff7a1a] mb-1.5 px-1">{aisle}</p>
          <ul className="space-y-1">
            {list.map((it, i) => {
              const src = it.price_source;
              const isLive = src === 'live' || src === 'cache';
              const isDerived = src === 'derived';
              return (
                <li
                  key={`${aisle}-${i}`}
                  className="flex items-center justify-between gap-2 text-[12px] text-zinc-200 rounded-lg bg-zinc-900/50 border border-zinc-800 px-3 py-2"
                  data-testid={`mp-item-${aisle}-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="truncate">{it.name}</p>
                      {isLive && (
                        <span className="shrink-0 text-[8px] font-black uppercase tracking-wider text-[#BEFF38] bg-[#BEFF38]/15 border border-[#BEFF38]/40 rounded px-1.5 py-0.5" data-testid={`mp-badge-live-${aisle}-${i}`}>Live</span>
                      )}
                      {isDerived && (
                        <span className="shrink-0 text-[8px] font-black uppercase tracking-wider text-sky-300 bg-sky-500/15 border border-sky-500/40 rounded px-1.5 py-0.5" data-testid={`mp-badge-derived-${aisle}-${i}`} title="Live Sainsbury's price scaled to your supermarket's typical price level">≈ Live</span>
                      )}
                    </div>
                    <p className="text-[10px] text-zinc-500 tabular-nums truncate">
                      {it.quantity}{it.unit}
                      {(isLive || isDerived) && it.product_name && ` · ${it.product_name.replace(/^~/, '')}`}
                    </p>
                  </div>
                  {it.product_url ? (
                    <a
                      href={it.product_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white font-bold tabular-nums shrink-0 hover:text-[#BEFF38]"
                      data-testid={`mp-item-price-${aisle}-${i}`}
                    >
                      £{Number(it.est_price_gbp || 0).toFixed(2)}
                    </a>
                  ) : (
                    <p className="text-white font-bold tabular-nums shrink-0" data-testid={`mp-item-price-${aisle}-${i}`}>
                      {!isLive && !isDerived && <span className="text-zinc-500">~</span>}
                      £{Number(it.est_price_gbp || 0).toFixed(2)}
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default function WeeklyMealPlanner() {
  const { toast } = useToast();
  const [prefs, setPrefs] = useState(null);
  const [plan, setPlan] = useState(null);
  const [tab, setTab] = useState('week');   // 'setup' | 'week' | 'shop'
  const [dayIdx, setDayIdx] = useState(0);
  const [busy, setBusy] = useState(false);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [refreshingPrices, setRefreshingPrices] = useState(false);
  const [eatenSet, setEatenSet] = useState(new Set());
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyBusy, setHistoryBusy] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [confirmForceOpen, setConfirmForceOpen] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState(null); // "dayIdx-slotIdx"

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      try {
        const [p, latest] = await Promise.all([
          api.mealPlannerPrefs(),
          api.latestWeeklyMealPlan(),
        ]);
        if (!alive) return;
        setPrefs(p);
        setPlan(latest || null);
        if (!latest) setTab('setup');
      } catch (e) {
        // silent — user probably new
      } finally {
        setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Poll live-price status while the background scraper is running.
  useEffect(() => {
    if (!plan?.id || plan.price_status !== 'pending') return undefined;
    let alive = true;
    const tick = async () => {
      try {
        const res = await api.getPlanPrices(plan.id);
        if (!alive) return;
        // Merge live shopping list into current plan
        setPlan((prev) => (prev ? {
          ...prev,
          shopping_list: res.shopping_list,
          est_total_gbp: res.est_total_gbp,
          price_status: res.price_status,
          live_price_count: res.live_price_count,
        } : prev));
        if (res.price_status === 'done') {
          if (res.live_price_count > 0) {
            toast({ title: 'Live prices ready', description: `${res.live_price_count} items priced from ${prefs?.supermarket || 'your supermarket'}.` });
          }
          return;
        }
      } catch {
        // silent — keep polling
      }
      if (alive) setTimeout(tick, 4000);
    };
    const t = setTimeout(tick, 3000);
    return () => { alive = false; clearTimeout(t); };
  }, [plan?.id, plan?.price_status, prefs?.supermarket, toast]);

  const handleSavePrefs = async () => {
    setSavingPrefs(true);
    try {
      const saved = await api.saveMealPlannerPrefs(prefs);
      setPrefs(saved);
      toast({ title: 'Preferences saved', description: 'Now generate your plan for the week.' });
      setTab('week');
    } catch (e) {
      toast({ title: 'Could not save', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleGenerate = async (opts = {}) => {
    const force = !!opts.force;
    setGenerating(true);
    try {
      const res = await api.generateWeeklyMealPlan('', force);
      setPlan(res);
      setDayIdx(0);
      setEatenSet(new Set());
      toast({ title: 'Weekly plan ready', description: `${res?.days?.length || 0} days · ${res?.shopping_list?.length || 0} items to buy.` });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && (detail?.code === 'plan_locked' || typeof detail === 'object')) {
        setConfirmForceOpen(true);
        return;
      }
      const msg = (typeof detail === 'string' ? detail : detail?.message) || e?.message || 'Generation failed';
      toast({ title: 'Could not generate', description: msg, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleLock = async () => {
    if (!plan?.id) return;
    setLockBusy(true);
    try {
      const res = plan.locked
        ? await api.unlockMealPlan(plan.id)
        : await api.lockMealPlan(plan.id);
      setPlan((prev) => ({ ...(prev || {}), ...res }));
      toast({
        title: res.locked ? 'Plan locked' : 'Plan unlocked',
        description: res.locked
          ? "Regenerate is blocked until you unlock. Your shopping is safe."
          : 'You can regenerate now if you want.',
      });
    } catch (e) {
      toast({ title: 'Could not update lock', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setLockBusy(false);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    setHistoryBusy(true);
    try {
      const res = await api.mealPlanHistory(20);
      setHistory(res.plans || []);
    } catch (e) {
      toast({ title: 'Could not load history', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setHistoryBusy(false);
    }
  };

  const handleRestore = async (planId) => {
    setHistoryBusy(true);
    try {
      const restored = await api.restoreMealPlan(planId);
      setPlan(restored);
      setDayIdx(0);
      setEatenSet(new Set());
      setHistoryOpen(false);
      toast({ title: 'Previous plan restored', description: 'And locked — no regenerate will overwrite it.' });
    } catch (e) {
      toast({ title: 'Restore failed', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setHistoryBusy(false);
    }
  };

  const handleEaten = async (day_date, recipe_name) => {
    try {
      await api.markMealEaten(plan.id, day_date, recipe_name);
      setEatenSet((s) => new Set(s).add(`${day_date}|${recipe_name}`));
      toast({ title: 'Logged', description: 'Added to today\u2019s macros.' });
      // Nudge the DailyMacroTracker to reload — same page, live update.
      window.dispatchEvent(new CustomEvent('freefitfuel:food-logged'));
    } catch (e) {
      toast({ title: 'Could not log', description: e?.message || 'Try again', variant: 'destructive' });
    }
  };

  const handleRegenerateMeal = async (day_index, slot_index, currentName) => {
    if (!plan?.id) return;
    const key = `${day_index}-${slot_index}`;
    setRegeneratingKey(key);
    try {
      const updated = await api.regenerateMeal(plan.id, day_index, slot_index);
      setPlan(updated);
      const swapped = updated?.days?.[day_index]?.recipes?.[slot_index]?.name;
      toast({
        title: 'Meal swapped',
        description: swapped && swapped !== currentName
          ? `${currentName} → ${swapped}. Shopping list updated.`
          : 'Shopping list updated.',
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || 'Try again';
      toast({ title: 'Could not swap meal', description: msg, variant: 'destructive' });
    } finally {
      setRegeneratingKey(null);
    }
  };

  const handleRefreshPrices = async () => {
    if (!plan?.id) return;
    setRefreshingPrices(true);
    try {
      const res = await api.refreshPlanPrices(plan.id);
      setPlan((prev) => (prev ? { ...prev, price_status: res.price_status || 'pending' } : prev));
      toast({ title: 'Refreshing prices…', description: `Re-scraping ${plan.prefs?.supermarket}` });
    } catch (e) {
      toast({ title: 'Refresh failed', description: e?.message || 'Try again', variant: 'destructive' });
    } finally {
      setRefreshingPrices(false);
    }
  };

  if (busy) {
    return (
      <Card title="Weekly meal plan" icon={ChefHat}>
        <div className="flex items-center gap-2 text-zinc-400 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Weekly meal plan"
      icon={ChefHat}
      action={(
        <div className="flex gap-2 flex-wrap justify-end">
          <button
            type="button"
            onClick={openHistory}
            data-testid="mp-open-history"
            className="h-9 px-2.5 rounded-full grid place-items-center bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-800 inline-flex items-center gap-1"
            aria-label="Restore a previous plan"
            title="Restore a previous plan"
          >
            <History className="w-3.5 h-3.5" />
            <span className="text-[11px] font-semibold uppercase tracking-wider hidden sm:inline">History</span>
          </button>
          {plan?.id && (
            <button
              type="button"
              onClick={handleToggleLock}
              disabled={lockBusy}
              data-testid="mp-lock-toggle"
              className={`h-9 px-3 rounded-full inline-flex items-center gap-1.5 border transition-colors ${
                plan.locked
                  ? 'bg-[#BEFF38] text-black border-[#BEFF38] hover:bg-lime-300'
                  : 'bg-zinc-900 text-zinc-200 border-zinc-800 hover:bg-zinc-800'
              }`}
              title={plan.locked
                ? "Plan is locked — no regenerate can overwrite it. Tap to unlock."
                : "Lock this plan so nothing overwrites it after you've shopped."}
            >
              {lockBusy
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : plan.locked ? <Lock className="w-3.5 h-3.5" /> : <LockOpen className="w-3.5 h-3.5" />}
              <span className="text-[11px] font-black uppercase tracking-wider">
                {plan.locked ? 'Locked' : "Lock — I've shopped"}
              </span>
            </button>
          )}
          <button
            type="button"
            onClick={() => setTab('setup')}
            data-testid="mp-open-setup"
            className="h-9 w-9 rounded-full grid place-items-center bg-zinc-900 hover:bg-zinc-800 text-zinc-200 border border-zinc-800"
            aria-label="Meal planner preferences"
          >
            <Settings2 className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => handleGenerate()}
            disabled={generating || !prefs}
            data-testid="mp-generate-btn"
            className="h-9 px-3 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-[11px] disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {plan ? 'Regenerate' : 'Generate'}
          </button>
        </div>
      )}
    >
      {tab === 'setup' && prefs && (
        <SetupPanel prefs={prefs} setPrefs={setPrefs} onSave={handleSavePrefs} saving={savingPrefs} />
      )}

      {tab !== 'setup' && plan && (
        <div className="space-y-4">
          {plan.locked && (
            <div
              data-testid="mp-locked-banner"
              className="flex items-center gap-2 rounded-2xl border border-[#BEFF38]/30 bg-[#BEFF38]/10 px-3 py-2"
            >
              <Lock className="w-3.5 h-3.5 text-[#BEFF38] shrink-0" />
              <p className="text-[11px] text-zinc-200 flex-1 leading-tight">
                <span className="font-semibold text-white">Locked plan</span> — Regenerate is disabled. Tap the lock button to unlock if you want to swap the week.
              </p>
            </div>
          )}
          <div className="flex gap-1.5 border border-zinc-800 bg-zinc-900/50 p-1 rounded-full">
            <button
              type="button"
              onClick={() => setTab('week')}
              data-testid="mp-tab-week"
              className={`flex-1 h-9 rounded-full text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 ${tab === 'week' ? 'bg-[#BEFF38] text-black' : 'text-zinc-400'}`}
            >
              <Utensils className="w-3.5 h-3.5" /> Week
            </button>
            <button
              type="button"
              onClick={() => setTab('shop')}
              data-testid="mp-tab-shop"
              className={`flex-1 h-9 rounded-full text-[11px] font-black uppercase tracking-wider inline-flex items-center justify-center gap-1.5 ${tab === 'shop' ? 'bg-[#BEFF38] text-black' : 'text-zinc-400'}`}
            >
              <ShoppingCart className="w-3.5 h-3.5" /> Shopping ({plan.shopping_list?.length || 0})
            </button>
          </div>

          {tab === 'week' && (
            <>
              <div className="flex gap-1 overflow-x-auto pb-1">
                {plan.days.map((d, i) => (
                  <button
                    key={d.date}
                    type="button"
                    onClick={() => setDayIdx(i)}
                    data-testid={`mp-day-tab-${i}`}
                    className={`shrink-0 h-9 px-3 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                      dayIdx === i ? 'bg-white text-black border-white' : 'bg-transparent text-zinc-400 border-zinc-800'
                    }`}
                  >
                    {new Date(d.date).toLocaleDateString('en-GB', { weekday: 'short' })}
                  </button>
                ))}
              </div>
              <DayView day={plan.days[dayIdx]} plan={plan} dayIndex={dayIdx} onEaten={handleEaten} eatenSet={eatenSet} onRegenerate={handleRegenerateMeal} regeneratingKey={regeneratingKey} />
              <p className="text-[10px] text-center text-zinc-500 pt-2">
                Week hits macros on <span className="text-[#BEFF38] font-bold">{plan.macro_fit_pct}%</span> of days · {plan.prefs.supermarket} · £{plan.prefs.weekly_budget_gbp} budget
              </p>
            </>
          )}

          {tab === 'shop' && (
            <ShoppingListView
              items={plan.shopping_list || []}
              budget={plan.prefs.weekly_budget_gbp}
              total={plan.est_total_gbp}
              supermarket={plan.prefs.supermarket}
              priceStatus={plan.price_status}
              liveCount={plan.live_price_count || 0}
              onRefresh={handleRefreshPrices}
              refreshing={refreshingPrices}
            />
          )}
        </div>
      )}

      {tab !== 'setup' && !plan && (
        <div className="py-6 text-center space-y-3" data-testid="mp-empty">
          <p className="text-zinc-400 text-sm">No plan yet. Tap Generate above for a full 7-day plan that hits your macros.</p>
          <button
            type="button"
            onClick={() => setTab('setup')}
            data-testid="mp-empty-setup"
            className="text-[11px] text-[#BEFF38] font-black uppercase tracking-wider hover:underline"
          >
            Or set your preferences first →
          </button>
          <div className="pt-2">
            <button
              type="button"
              onClick={openHistory}
              data-testid="mp-empty-open-history"
              className="text-[11px] text-zinc-300 font-semibold uppercase tracking-wider hover:underline inline-flex items-center gap-1.5"
            >
              <History className="w-3.5 h-3.5" /> Restore a previous plan
            </button>
          </div>
        </div>
      )}

      {historyOpen && (
        <div
          className="fixed inset-0 z-[10020] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
          onClick={() => setHistoryOpen(false)}
          data-testid="mp-history-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg bg-zinc-900 border border-zinc-800 rounded-t-3xl sm:rounded-3xl p-5 text-white max-h-[85vh] overflow-y-auto"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <p className="text-[#BEFF38] text-[11px] uppercase tracking-wider font-black">Meal plan history</p>
                <h3 className="text-lg font-bold mt-0.5">Restore a previous plan</h3>
                <p className="text-[11px] text-zinc-400 mt-1 leading-snug">
                  Tap any plan to restore it as your current one. It will be locked so nothing overwrites it.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                data-testid="mp-history-close"
                className="h-8 w-8 rounded-full grid place-items-center bg-zinc-800 hover:bg-zinc-700"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {historyBusy && (
              <div className="flex items-center gap-2 text-zinc-400 text-sm py-3"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            )}
            {!historyBusy && history.length === 0 && (
              <p className="text-zinc-500 text-sm py-6 text-center">No previous plans found for this device.</p>
            )}
            {!historyBusy && history.length > 0 && (
              <ul className="space-y-2">
                {history.map((h, i) => (
                  <li key={h.id} className="rounded-2xl border border-zinc-800 bg-zinc-950 p-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-white font-semibold text-sm">
                            Week of {h.week_start || '—'}
                          </p>
                          {i === 0 && (
                            <span className="text-[9px] uppercase tracking-wider font-black text-[#BEFF38] bg-[#BEFF38]/10 border border-[#BEFF38]/30 rounded-full px-1.5 py-0.5">Current</span>
                          )}
                          {h.locked && (
                            <span className="text-[9px] uppercase tracking-wider font-black text-white bg-[#BEFF38]/25 border border-[#BEFF38]/30 rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5">
                              <Lock className="w-2.5 h-2.5" /> Locked
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-zinc-400 mt-0.5 leading-tight">
                          {h.prefs_supermarket || 'Shop'} · {h.shopping_items_count || 0} items · £{Number(h.est_total_gbp || 0).toFixed(2)} · Macro fit {h.macro_fit_pct || 0}%
                        </p>
                        <p className="text-[10px] text-zinc-500 mt-0.5">
                          Created {new Date(h.created_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {i !== 0 && (
                        <button
                          type="button"
                          onClick={() => handleRestore(h.id)}
                          disabled={historyBusy}
                          data-testid={`mp-history-restore-${i}`}
                          className="shrink-0 h-8 px-3 rounded-full bg-[#BEFF38] text-black font-black uppercase tracking-wider text-[10px] disabled:opacity-50 inline-flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" /> Restore
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {confirmForceOpen && (
        <div
          className="fixed inset-0 z-[10030] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center px-4"
          onClick={() => setConfirmForceOpen(false)}
          data-testid="mp-force-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-zinc-900 border border-orange-500/30 rounded-t-3xl sm:rounded-3xl p-5 text-white"
            style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 1.25rem)' }}
          >
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <p className="text-orange-300 text-[11px] uppercase tracking-wider font-black">Plan is locked</p>
            </div>
            <h3 className="text-lg font-bold">Overwrite the plan you&apos;ve shopped for?</h3>
            <p className="text-sm text-zinc-300 mt-2 leading-snug">
              This plan is locked because you&apos;ve committed to shopping for it. Regenerating will replace it with a brand new week.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => setConfirmForceOpen(false)}
                data-testid="mp-force-cancel"
                className="flex-1 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 text-white text-sm font-semibold"
              >
                Keep locked
              </button>
              <button
                type="button"
                onClick={() => { setConfirmForceOpen(false); handleGenerate({ force: true }); }}
                data-testid="mp-force-confirm"
                className="flex-1 h-11 rounded-full bg-orange-500 hover:bg-orange-400 text-black text-sm font-black uppercase tracking-wider"
              >
                Regenerate anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
